import { useEffect, useState } from "react";
import React from "react";
import { Box, FormControl } from "@mui/material";
import CustomTextField from "../../../main/widgets/CustomTextField";
import { Typography, Card, CardContent } from "@mui/joy";
import Grid from "@mui/joy/Grid";
import Button from "@mui/joy/Button";
import Snackbar from "@mui/joy/Snackbar";
import {
  postCustomer,
  putCustomer,
  readCustomer,
} from "../../../main/API/customer";
import { primaryColor } from "../../../main/theme/constants";
import "react-international-phone/build/index.css";
import { MuiPhone } from "../../../main/widgets/MuiPhone";
import PermissionGate from "../../../main/components/PermissionGate";
import { PERMISSIONS } from "../../../main/context/AuthContext";
import { usePermissions } from "../../../main/hooks/usePermissions";

// Light design tokens
const formStyles = {
  pageBg: "#f8fafc",
  cardBg: "#ffffff",
  cardShadow: "0 1px 3px rgba(0,0,0,0.06)",
  cardRadius: "16px",
  cardBorder: "1px solid rgba(0,0,0,0.06)",
  sectionSpacing: 3,
  labelColor: "#475569",
  titleColor: "#1e293b",
};

export default function CustomerForm({ formID }) {
  const { hasPermission } = usePermissions();
  const [formData, setFormData] = useState({
    id: "",
    first_name: "",
    last_name: "",
    first_name_ar: "",
    last_name_ar: "",
    phone_number: "+968",
    id_number: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const phoneHandleChange = (value) => {
    setFormData({
      ...formData,
      phone_number: value,
    });
  };

  const [isLoading, setLoading] = useState(false);
  const [successUpdate, setSuccessUpdate] = useState(false);
  const [failedUpdate, setFailedUpdate] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (formID == null) {
      if (!hasPermission(PERMISSIONS.CUSTOMERS_CREATE)) return;
    } else if (!hasPermission(PERMISSIONS.CUSTOMERS_UPDATE)) {
      return;
    }

    setLoading(true);

    if (formID == null) {
      try {
        const response = await postCustomer(formData);
        if (response.status != 201) {
          setFailedUpdate(true);
          setErrorMessage(response.statusText);
        } else {
          window.location.href = "/Customers/" + response.data.id + "/EditCustomer";
        }
      } catch (e) {
        setFailedUpdate(true);
        setErrorMessage(e?.message || String(e));
      }
    } else {
      try {
        const response = await putCustomer(formData);
        if (response.status == 200) {
          setSuccessUpdate(true);
        } else {
          setFailedUpdate(true);
          setErrorMessage(response.statusText);
        }
      } catch (e) {
        setFailedUpdate(true);
        setErrorMessage(e?.message || String(e));
      }
    }
    setLoading(false);
  };

  const fillFormData = (customer) => {
    setFormData(customer);
  };

  const getCustomer = async (CustomerID) => {
    if (!hasPermission(PERMISSIONS.CUSTOMERS_VIEW)) return;
    await readCustomer(CustomerID)
      .then((response) => response.data)
      .then((data) => fillFormData(data))
      .catch((err) => console.error(err?.message || err));
  };

  useEffect(() => {
    if (formID != null) {
      getCustomer(formID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isEditMode = formID != null;

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: formStyles.pageBg,
        py: 3,
        px: { xs: 2, sm: 3 },
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Typography
          level="h3"
          sx={{
            color: formStyles.titleColor,
            fontWeight: 700,
            mb: 3,
            fontSize: { xs: "1.5rem", sm: "1.75rem" },
          }}
        >
          {isEditMode ? "Update Customer" : "Add Customer"}
        </Typography>

        <Card
          variant="plain"
          sx={{
            bgcolor: formStyles.cardBg,
            boxShadow: formStyles.cardShadow,
            borderRadius: formStyles.cardRadius,
            border: formStyles.cardBorder,
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
            <form onSubmit={handleSubmit}>
              <FormControl fullWidth>
                {/* ID (edit mode only) */}
                {isEditMode && (
                  <Box sx={{ mb: formStyles.sectionSpacing }}>
                    <CustomTextField
                      name="id"
                      display="block"
                      disabled
                      type="text"
                      onChange={handleChange}
                      value={formData.id}
                      label="ID"
                      xs={12}
                    />
                  </Box>
                )}

                {/* Names row */}
                <Typography
                  level="body2"
                  sx={{
                    color: formStyles.labelColor,
                    fontWeight: 600,
                    mb: 1.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    fontSize: "0.75rem",
                  }}
                >
                  Name
                </Typography>
                <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                  <Grid xs={12} sm={6}>
                    <CustomTextField
                      slotProps={{ input: { minLength: 1, maxLength: 30 } }}
                      name="first_name"
                      required
                      type="text"
                      form
                      label="First Name"
                      onChange={handleChange}
                      value={formData.first_name}
                      xs={12}
                    />
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <CustomTextField
                      slotProps={{ input: { minLength: 1, maxLength: 30 } }}
                      name="last_name"
                      type="text"
                      form
                      label="Last Name"
                      onChange={handleChange}
                      value={formData.last_name}
                      xs={12}
                    />
                  </Grid>
                </Grid>

                {/* Arabic names */}
                <Typography
                  level="body2"
                  sx={{
                    color: formStyles.labelColor,
                    fontWeight: 600,
                    mb: 1.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    fontSize: "0.75rem",
                  }}
                >
                  Name (Arabic)
                </Typography>
                <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                  <Grid xs={12} sm={6}>
                    <CustomTextField
                      slotProps={{ input: { minLength: 1, maxLength: 30 } }}
                      name="first_name_ar"
                      required
                      type="text"
                      form
                      label="First Name (Arabic)"
                      onChange={handleChange}
                      value={formData.first_name_ar}
                      xs={12}
                    />
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <CustomTextField
                      slotProps={{ input: { minLength: 1, maxLength: 30 } }}
                      name="last_name_ar"
                      type="text"
                      form
                      label="Last Name (Arabic)"
                      onChange={handleChange}
                      value={formData.last_name_ar}
                      xs={12}
                    />
                  </Grid>
                </Grid>

                {/* ID Number & Phone */}
                <Typography
                  level="body2"
                  sx={{
                    color: formStyles.labelColor,
                    fontWeight: 600,
                    mb: 1.5,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    fontSize: "0.75rem",
                  }}
                >
                  Contact & ID
                </Typography>
                <Grid container spacing={2} sx={{ mb: formStyles.sectionSpacing }}>
                  <Grid xs={12} sm={6}>
                    <CustomTextField
                      slotProps={{ input: { minLength: 1, maxLength: 50 } }}
                      name="id_number"
                      type="text"
                      form
                      label="ID Number"
                      onChange={handleChange}
                      value={formData.id_number}
                      xs={12}
                    />
                  </Grid>
                  <Grid xs={12} sm={6}>
                    <MuiPhone
                      required
                      value={formData.phone_number}
                      onChange={phoneHandleChange}
                    />
                  </Grid>
                </Grid>

                {/* Actions */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    pt: 2,
                    borderTop: "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  {isEditMode ? (
                    <PermissionGate permission={PERMISSIONS.CUSTOMERS_UPDATE}>
                      <Button
                        type="submit"
                        loading={isLoading}
                        sx={{
                          bgcolor: primaryColor,
                          "&:hover": { bgcolor: "#4b059a" },
                          borderRadius: "10px",
                          px: 3,
                          fontWeight: 600,
                        }}
                      >
                        Update
                      </Button>
                    </PermissionGate>
                  ) : (
                    <PermissionGate permission={PERMISSIONS.CUSTOMERS_CREATE}>
                      <Button
                        type="submit"
                        loading={isLoading}
                        sx={{
                          bgcolor: primaryColor,
                          "&:hover": { bgcolor: "#4b059a" },
                          borderRadius: "10px",
                          px: 3,
                          fontWeight: 600,
                        }}
                      >
                        Submit
                      </Button>
                    </PermissionGate>
                  )}
                </Box>
              </FormControl>
            </form>
          </CardContent>
        </Card>
      </Box>

      <Snackbar
        autoHideDuration={3000}
        open={successUpdate}
        variant="soft"
        color="success"
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setSuccessUpdate(false);
        }}
      >
        Customer updated successfully
      </Snackbar>
      <Snackbar
        autoHideDuration={3000}
        open={failedUpdate}
        variant="soft"
        color="danger"
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setFailedUpdate(false);
        }}
      >
        {"Update failed. " + (errorMessage || "Please try again.")}
      </Snackbar>
    </Box>
  );
}
